import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IonContent,
  IonLabel,
  IonPage,
  IonSegment,
  IonSegmentButton,
  useIonRouter,
} from '@ionic/react';
import { ChevronRight } from 'lucide-react';
import { TopBar } from '../components/ui/TopBar';
import { useCollapsingTitle } from '../hooks/useCollapsingTitle';
import { EarningsHistoryRow } from '../components/earnings/EarningsHistoryRow';
import {
  PayoutsRegisterModal,
  PayoutsRegisterPlaceholder,
} from '../components/earnings/PayoutsRegisterModal';
import { useMyEarnings } from '../hooks/useEarnings';
import { formatPriceCents } from '../utils/currency';
import { sumEarnedCents } from '../utils/earnings';
import type { EarningsRow } from '../types/earnings';

type EarningsTab = 'pending' | 'paidOut';

const TABS: readonly { key: EarningsTab; labelKey: string }[] = [
  { key: 'pending', labelKey: 'earnings.tabs.pending' },
  { key: 'paidOut', labelKey: 'earnings.tabs.paidOut' },
] as const;

/**
 * My Earnings — chef-only page mirroring My Kitchen's layout: two segments,
 * infinite-ish list under each, single data hook per segment.
 *
 * Pending shows every captured / captured-partial / cancelled row from
 * GET /orders/earnings (each an EarningsRow with an optional snapshot).
 *
 * The "Total earned" card sits above the segments. It sums grossCents across
 * rows that actually earned (captured + captured-partial) and is the figure
 * the page exists to show, so it leads rather than trailing the Pending list.
 * It renders on both segments and at $0.00, so switching tabs or landing as a
 * brand-new chef never reflows the content under it. Tapping it pops the
 * PayoutsRegisterModal until the payout flow lands; see the Paid Out segment
 * for the same placeholder in its permanent home.
 *
 * The page is gated to chefs by the profile-menu row that lands people
 * here; a non-chef reaching this URL directly still sees a functional
 * page (empty Pending list, Paid Out placeholder). No additional guard
 * inside — keeping the page open to non-chefs makes the URL shareable
 * later without a re-plumbing.
 */
export const MyEarningsPage: React.FC = () => {
  const { t } = useTranslation('profile');
  const router = useIonRouter();
  const [tab, setTab] = useState<EarningsTab>('pending');
  const [payoutsModalOpen, setPayoutsModalOpen] = useState(false);
  const { data: rows, isLoading } = useMyEarnings();

  // Shared with the profile menu's sub line (see utils/earnings) so the two
  // surfaces can't quote different figures.
  const totalEarnedCents = sumEarnedCents(rows);

  // Title band hides on scroll-down, returns on scroll-up. The ref is the
  // hook's window onto the scroll element (this page has no PullToRefresh
  // to already own one).
  const contentRef = useRef<HTMLIonContentElement>(null);
  const { collapsed, onScroll } = useCollapsingTitle(contentRef);

  return (
    <IonPage>
      <TopBar title={t('menu.earnings')} titleCollapsed={collapsed} />
      <IonContent ref={contentRef} scrollEvents onIonScroll={onScroll}>
        <div className="mx-auto max-w-lg px-4 pt-4 pb-2 space-y-4">
          {/* Total earned leads the page. It's the figure a chef opens My
              Earnings to see, so it reads before the segments rather than
              after a scroll to the end of the Pending list. Always rendered
              (a chef with no settled sales sees $0.00) so the header never
              pops in and reflows the list under it. */}
          <button
            type="button"
            onClick={() => setPayoutsModalOpen(true)}
            className="hp-earnings-total"
            aria-label={`${t('earnings.totalEarnedLabel')} ${formatPriceCents(
              totalEarnedCents,
              { zeroAsAmount: true },
            )}`}
          >
            <span className="hp-earnings-total__body">
              <span className="hp-earnings-total__label">
                {t('earnings.totalEarnedLabel')}
              </span>
              <span className="hp-earnings-total__amount">
                {formatPriceCents(totalEarnedCents, { zeroAsAmount: true })}
              </span>
            </span>
            <ChevronRight
              className="hp-earnings-total__chevron h-5 w-5"
              aria-hidden="true"
            />
          </button>

          <IonSegment
            value={tab}
            onIonChange={(e) => setTab(e.detail.value as EarningsTab)}
          >
            {TABS.map(({ key, labelKey }) => (
              <IonSegmentButton key={key} value={key}>
                <IonLabel>{t(labelKey)}</IonLabel>
              </IonSegmentButton>
            ))}
          </IonSegment>

          {tab === 'pending' && (
            <>
              {isLoading ? null : (rows ?? []).length === 0 ? (
                <p className="py-10 text-center text-sm text-muted-foreground">
                  {t('earnings.empty.pending')}
                </p>
              ) : (
                <div>
                  {(rows ?? []).map((row: EarningsRow) => (
                    <EarningsHistoryRow
                      key={row.payment.id}
                      row={row}
                      onClick={() =>
                        router.push(`/listing/${row.listing.id}`, 'forward')
                      }
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {tab === 'paidOut' && (
            <div className="py-6">
              <PayoutsRegisterPlaceholder />
            </div>
          )}
        </div>
      </IonContent>
      <PayoutsRegisterModal
        isOpen={payoutsModalOpen}
        onClose={() => setPayoutsModalOpen(false)}
      />
    </IonPage>
  );
};
